import React from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { FaRocket, FaHandshake, FaUsers } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { MdOutlineLightbulb, MdOutlineFlag, MdOutlinePerson } from 'react-icons/md';

const journeyData = [
    {
        date: '2021',
        title: 'Founded',
        description: 'Mentorsity was founded to help students, professionals, and businesses.',
        icon: <FaRocket />,
        type: 'work'
    },
    {
        date: '220+',
        title: 'Companies',
        description: 'Forged partnerships with over 220 companies.',
        icon: <FaHandshake />,
        type: 'work'
    },
    {
        date: '150k+',
        title: 'Professionals',
        description: 'Positively impacted over 150,000 lives.',
        icon: <FaUsers />,
        type: 'education'
    },
];

const JourneyTimeline = () => {
    return (
        <section className="mt-8 max-w-[1200px] mx-auto">
            <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <p className="mt-4 text-base text-gray-700 leading-relaxed max-w-3xl mx-auto">
                    Mentorsity is a mentoring-based learning ecosystem for students, professionals, and businesses that offers peer learning to earn, learn, and grow together.
                </p>
            </motion.div>

            <VerticalTimeline lineColor="rgb(4, 35, 107)">
                {journeyData.map((item, index) => (
                    <VerticalTimelineElement
                        key={index}
                        className="vertical-timeline-element"
                        date={item.date}
                        dateClassName="text-base font-bold text-primary-color"
                        iconStyle={{ 
                            background: 'rgb(244, 180, 26)',
                            boxShadow: '0 0 0 4px white, 0 0 0 8px rgba(244, 180, 26, 0.1)'
                        }}
                        icon={item.icon}
                        contentStyle={{
                            background: 'white',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            borderRadius: '1rem',
                            padding: '1.5rem'
                        }}
                        contentArrowStyle={{ borderRight: '7px solid white' }}
                    >
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                        >
                            <h3 className="text-xl font-bold text-primary-color mb-2">{item.title}</h3>
                            <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
                        </motion.div>
                    </VerticalTimelineElement>
                ))}
            </VerticalTimeline>
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {/* Vision Card */}
                    <motion.div 
                        className="group relative bg-white rounded-xl overflow-hidden shadow-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-color/10 to-secondary-color/10"></div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-color to-secondary-color flex items-center justify-center text-white">
                                    <MdOutlineLightbulb className="text-xl" />
                                </div>
                                <h2 className="text-xl font-bold bg-gradient-to-r from-primary-color to-secondary-color bg-clip-text text-transparent">Vision</h2>
                            </div>
                            <p className="text-base text-gray-600 leading-relaxed">
                                Our vision is to create the world's largest community of passionate learners sharing interests and mindsets. Together with mentors, we aim to build a powerful, inclusive environment that sparks change in society through shared knowledge and motivation.
                            </p>
                        </div>
                    </motion.div>

                    {/* Mission Card */}
                    <motion.div 
                        className="group relative bg-white rounded-xl overflow-hidden shadow-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary-color/10 to-primary-color/10"></div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-secondary-color to-primary-color flex items-center justify-center text-white">
                                    <MdOutlineFlag className="text-xl" />
                                </div>
                                <h2 className="text-xl font-bold bg-gradient-to-r from-secondary-color to-primary-color bg-clip-text text-transparent">Mission</h2>
                            </div>
                            <p className="text-base text-gray-600 leading-relaxed">
                                A mentor is an experienced guide. We believe everyone needs support to grow. Mentors help students build academic and social confidence, and education becomes transformative when it builds character and life skills.
                            </p>
                        </div>
                    </motion.div>

                    {/* Founder's Message Card */}
                    <motion.div 
                        className="group relative bg-white rounded-xl overflow-hidden shadow-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-color/10 to-secondary-color/10"></div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-color to-secondary-color flex items-center justify-center text-white">
                                    <MdOutlinePerson className="text-xl" />
                                </div>
                                <h2 className="text-xl font-bold bg-gradient-to-r from-primary-color to-secondary-color bg-clip-text text-transparent">Founder's Message</h2>
                            </div>
                            <p className="text-base text-gray-600 leading-relaxed">
                                I believe success follows passion. We're creating a nurturing ecosystem for learners to interact, grow, and find opportunities to connect with industry leaders. Our mission is to ensure every learner receives a personalized, gamified experience to achieve their fullest potential.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default JourneyTimeline;